import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const OnlineBar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [online, setOnline] = useState([]);
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 8 });
  const closedRef = useRef(false);
  const dragRef = useRef(null);
  const movedRef = useRef(false);

  useEffect(() => {
    if (!user || !user.onboarded) return;
    let active = true;
    const fetchOnline = async () => {
      try {
        const { data } = await api.get("/users/online");
        if (!active) return;
        const list = Array.isArray(data) ? data : [];
        setOnline(list);
        if (list.length === 0) setVisible(false);
        else if (!closedRef.current) setVisible(true);
      } catch (e) {}
    };
    fetchOnline();
    const iv = setInterval(fetchOnline, 45000);
    return () => { active = false; clearInterval(iv); };
  }, [user]);

  if (!user || !visible || online.length === 0 || pathname.startsWith("/admin")) return null;

  const close = (e) => {
    e.stopPropagation();
    closedRef.current = true;
    setVisible(false);
  };

  const openProfile = (e, id) => {
    e.stopPropagation();
    setMinimized(true);
    if (!user.premium) {
      navigate("/premium");
      return;
    }
    navigate(`/decouvrir?user=${id}`);
  };

  const startDrag = (e, el) => {
    const rect = el.getBoundingClientRect();
    movedRef.current = false;
    dragRef.current = {
      sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y,
      minX: pos.x - (rect.left - 8),
      maxX: pos.x + (window.innerWidth - rect.right - 8),
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (Math.abs(dx) + Math.abs(dy) > 5) movedRef.current = true;
    const maxY = Math.max(window.innerHeight - 170, 8);
    setPos({
      x: Math.min(Math.max(d.px + dx, d.minX), d.maxX),
      y: Math.min(Math.max(d.py + dy, 8), maxY),
    });
  };
  const endDrag = () => { dragRef.current = null; };

  const onMiniClick = () => {
    if (movedRef.current) { movedRef.current = false; return; }
    setPos((p) => ({ ...p, x: 0 }));
    setMinimized(false);
  };

  if (minimized) {
    return (
      <button
        data-testid="online-bar-mini"
        onPointerDown={(e) => startDrag(e, e.currentTarget)}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={onMiniClick}
        style={{ top: pos.y, transform: `translateX(${pos.x}px)`, touchAction: "none" }}
        className="fixed right-2 z-[65] bg-kora-ink text-white rounded-full pl-2.5 pr-3 py-2 shadow-[0_8px_26px_rgba(43,31,23,0.45)] border border-white/10 flex items-center gap-1.5 cursor-grab active:cursor-grabbing"
        aria-label="Membres en ligne"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
        </span>
        <span className="text-xs font-black">{online.length}</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-x-0 z-[65] px-3 pointer-events-none" style={{ top: pos.y }}>
      <div
        data-testid="online-bar"
        style={{ transform: `translateX(${pos.x}px)` }}
        className="pointer-events-auto max-w-md mx-auto bg-kora-ink text-white rounded-2xl shadow-[0_10px_34px_rgba(43,31,23,0.45)] border border-white/10 px-3.5 py-2.5"
      >
        <div
          data-testid="online-bar-handle"
          onPointerDown={(e) => startDrag(e, e.currentTarget.parentElement)}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          style={{ touchAction: "none" }}
          className="flex items-center gap-2 cursor-grab active:cursor-grabbing select-none"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
          </span>
          <p className="text-xs font-bold flex-1" data-testid="online-bar-title">
            En ligne maintenant, cliquez sur le profil pour discuter
          </p>
          <button
            data-testid="online-bar-minimize"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setMinimized(true); }}
            aria-label="Réduire"
            className="text-white/60 hover:text-white shrink-0 p-1"
          >
            <span className="block w-3.5 h-[2.5px] bg-current rounded-full" />
          </button>
          <button
            data-testid="online-bar-close"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={close}
            aria-label="Fermer"
            className="text-white/60 hover:text-white shrink-0 p-1 -mr-1"
          >
            <X size={16} />
          </button>
        </div>

        <div data-testid="online-bar-expanded" className="flex gap-3 mt-3 pb-1 overflow-x-auto">
          {online.map((o) => (
            <button
              key={o.user_id}
              data-testid={`online-profile-${o.user_id}`}
              onClick={(e) => openProfile(e, o.user_id)}
              className="flex flex-col items-center shrink-0 w-16"
            >
              <span className="relative">
                {o.photo_url ? (
                  <img src={resolveImg(o.photo_url)} alt={o.name} className="w-14 h-14 rounded-full object-cover border-2 border-kora-terracotta" />
                ) : (
                  <span className="w-14 h-14 rounded-full bg-kora-terracotta border-2 border-white/30 flex items-center justify-center text-lg font-black">{(o.name || "?")[0]}</span>
                )}
                <span className="absolute bottom-0 right-0.5 w-3.5 h-3.5 rounded-full bg-green-400 border-2 border-kora-ink" />
              </span>
              <span className="text-[11px] font-semibold mt-1 truncate w-full text-center">{o.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnlineBar;
